import dayjs from 'dayjs'
import React, { useEffect, useState, useMemo } from 'react'
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material'
import Print from '@mui/icons-material/Print'
import ConnectingLines from 'src/consumer/components/LiveSessionPage/components/ConnectingLines'
import RenderNoteOverReport from 'src/consumer/components/LiveSessionPage/components/RenderNoteOverReport/RenderNoteOverReport'
import { ViewOnlyPrescription } from 'src/consumer/components/MyLiveSessions'
import PrintableScreeningReportComponent from 'src/mentor/components/ScreeningReportMakingPage/printableReport/PrintableScreeningReportComponent'
import TherapyReportPreview from 'src/mentor/components/TherapyReportMakingPage/components/TherapyReportPreview'
import AssessmentReportHistoryView from './AssessmentReportHistoryView'
import EventAdditionalData from './EventAdditionalData'
import ImageWithMenu from 'src/mentor/components/PrescriptionMakingPage/printComponents/prescriptionImageRender/components/ImageWithMenu'
import usePagedPrintHistory from 'src/mentor/components/PrescriptionMakingPage/hooks/usePagedPrintHistory'
import { parseReportTimestamp } from 'src/mentor/components/TherapyReportMakingPage/TherapyReportMakingPageComponents'

/** Get time and label for timeline marker; same order as sortedReports. */
function getTimeAndLabel(report) {
  if (report?.apiParam === 'meta-prescription') {
    return {
      time: report?.prescriptionData?.startTime || report?.prescriptionData?.createdTime || report?.prescriptionData?.modifiedTime,
      label: 'Consultation',
    }
  }
  if (report?.therapyReport) {
    return {
      time: report?.therapyReport?.createdTime || report?.therapyReport?.modifiedTime,
      label: 'Therapy',
    }
  }
  if (report?.assessmentReport) {
    const runs = report?.assessmentRuns ?? report?.assessmentReport?.assessmentRuns ?? []
    const latestRun = runs.length > 0
      ? [...runs].sort((a, b) => (parseReportTimestamp(b?.completedAt)?.valueOf() ?? 0) - (parseReportTimestamp(a?.completedAt)?.valueOf() ?? 0))[0]
      : null
    return {
      time: latestRun?.completedAt || report?.assessmentReport?.createdTime || report?.assessmentReport?.modifiedTime,
      label: 'Assessment',
    }
  }
  if (report?.screeningReport) {
    return {
      time: report?.screeningReport?.createdTime || report?.screeningReport?.modifiedTime,
      label: 'Screening',
    }
  }
  return { time: null, label: 'Report' }
}

export default function VisitReportsComponent({
  visitNo,
  date,
  reports,
  screeningSections = [],
  allVitalsOfThisPatient,
  setIsPrintingPastHistoryReports = () => {},
  prescriptionViewSettings,
  hideSendToDoctorButton = false,
  hidePrintButton = false,
  hideOpenPrescriptionEvent = false, // Hide "View/Edit Consultation" button
  mentorPrivateNotes = [],
  patientId,
  clinicId,
  role = 'doctor',
  hidePrivateNotes = false, // Hide private notes fetching
  mentorId = null, // Mentor ID for clinic assistant portal
  patientHistoryGetApiSignature = null, // API signature for detecting clinic assistant context
  doctorSpecializations = null, // Doctor specializations for fetching screening sections
  visitType = null, // Visit type ('new' or 'repeat') for fetching screening sections
  sectionIds = null, // Optional array of ids for scroll-spy (one per report, same order as sortedReports)
  vitalsByBookedEventId = {},
  useBatchedVitals = false,
  batchedVitalsLoading = false,
  batchedVitalsReady = false,
}) {
  // Paged.js print hook for individual reports
  const { printSingleReport, isPrinting: isPagedPrinting } = usePagedPrintHistory()
  const [activePrintReportKey, setActivePrintReportKey] = useState(null)
  
  // Create refs map for report elements
  const reportRefs = React.useRef({})
  
  const sortedReports = useMemo(() => [...reports].sort((a, b) => {
    let dateA = a.createdTime || a.modifiedTime
    let dateB = b.createdTime || b.modifiedTime

    if (a.apiParam === 'meta-prescription') {
      dateA =
        a?.prescriptionData?.startTime ||
        a?.prescriptionData?.createdTime ||
        a.prescriptionData?.createdTime ||
        a.prescriptionData?.modifiedTime
    }
    if (b.apiParam === 'meta-prescription') {
      dateB =
        b?.prescriptionData?.startTime ||
        b?.prescriptionData?.createdTime ||
        b.prescriptionData?.createdTime ||
        b.prescriptionData?.modifiedTime
    }
    if (a?.type === 'therapy-report-response') {
      dateA =
        a?.therapyReport?.createdTime ||
        a?.therapyReport?.modifiedTime
    }
    if (b?.type === 'therapy-report-response') {
      dateB =
        b?.therapyReport?.createdTime ||
        b?.therapyReport?.modifiedTime
    }
    if (a?.type === 'screening-report-response') {
      dateA =
        a?.screeningReport?.createdTime ||
        a?.screeningReport?.modifiedTime
    }
    if (b?.type === 'screening-report-response') {
      dateB =
        b?.screeningReport?.createdTime ||
        b?.screeningReport?.modifiedTime
    }
    if (a?.type === 'assessment-report-response') {
      const runsA = a?.assessmentRuns ?? a?.assessmentReport?.assessmentRuns ?? []
      const latestA = runsA.length > 0 ? [...runsA].sort((x, y) => (parseReportTimestamp(y?.completedAt)?.valueOf() ?? 0) - (parseReportTimestamp(x?.completedAt)?.valueOf() ?? 0))[0] : null
      dateA = latestA?.completedAt || a?.assessmentReport?.createdTime || a?.assessmentReport?.modifiedTime
    }
    if (b?.type === 'assessment-report-response') {
      const runsB = b?.assessmentRuns ?? b?.assessmentReport?.assessmentRuns ?? []
      const latestB = runsB.length > 0 ? [...runsB].sort((x, y) => (parseReportTimestamp(y?.completedAt)?.valueOf() ?? 0) - (parseReportTimestamp(x?.completedAt)?.valueOf() ?? 0))[0] : null
      dateB = latestB?.completedAt || b?.assessmentReport?.createdTime || b?.assessmentReport?.modifiedTime
    }

    const parsedA = parseReportTimestamp(dateA)
    const parsedB = parseReportTimestamp(dateB)
    const momentA = parsedA?.isValid() ? parsedA : dayjs(dateA)
    const momentB = parsedB?.isValid() ? parsedB : dayjs(dateB)
    return momentB.valueOf() - momentA.valueOf() // Sort descending (newest first)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [reports])

  // Build a lightweight map from therapy report snapshots.
  // This avoids eager network fanout; TherapyReportPreview fetches lazily only when needed.
  const [nextVisitSlotMap, setNextVisitSlotMap] = useState({})
  useEffect(() => {
    const snapshotEntries = (sortedReports || [])
      .map((r) => {
        const therapyReport = r?.therapyReport
        if (!therapyReport) return null
        const eventId =
          therapyReport?.nextVisitReferenceBookedEventId || therapyReport?.nextVisitBookedEventId || null
        if (!eventId) return null
        const snapshot = {
          id: eventId,
          status:
            therapyReport?.nextVisitStatus ||
            therapyReport?.nextVisitBookedEventStatus ||
            therapyReport?.nextVisit?.status ||
            null,
          startTime:
            therapyReport?.nextVisitStartTime ||
            therapyReport?.nextVisitBookedEventStartTime ||
            therapyReport?.nextVisit?.startTime ||
            therapyReport?.nextVisitDate ||
            null,
          endTime:
            therapyReport?.nextVisitEndTime ||
            therapyReport?.nextVisitBookedEventEndTime ||
            therapyReport?.nextVisit?.endTime ||
            null,
          timeSlot: therapyReport?.nextVisitTimeSlot || therapyReport?.nextVisit?.timeSlot || null,
          meetingSrc: therapyReport?.nextVisitMeetingSrc || therapyReport?.nextVisitMode || therapyReport?.nextVisit?.meetingSrc || null,
          eventType: therapyReport?.nextVisitEventType || therapyReport?.nextVisit?.eventType || null,
          chamberRoomName:
            therapyReport?.nextVisitChamberRoomName || therapyReport?.nextVisit?.chamberRoomName || null,
          timezone: therapyReport?.nextVisitTimezone || therapyReport?.nextVisit?.timezone || null,
          eventDuration:
            therapyReport?.nextVisitEventDuration || therapyReport?.nextVisit?.eventDuration || null,
        }
        return [eventId, snapshot]
      })
      .filter(Boolean)
    setNextVisitSlotMap(Object.fromEntries(snapshotEntries))
  }, [sortedReports])

  const reportContainerStyles = {
    position: 'relative',
    // A4 width (794px) + the 12px left indent below. Keeping the indent as padding on
    // a 794px box used to shrink the content area to ~782px while the inner A4 still
    // rendered at ~793.5px — overflowing by ~12px and triggering a spurious horizontal
    // scrollbar on the past-history report even on wide screens. Widening the box by the
    // indent keeps the content area at the full A4 width so it fits exactly.
    maxWidth: 'calc(794px + 12px)',
    width: '100%',
    margin: '0 auto',
    paddingTop: '8px',
    paddingRight: '0px',
    paddingBottom: '24px',
    paddingLeft: '12px',
  }

  const timeMarkers = (sortedReports || []).map(getTimeAndLabel)

  useEffect(() => {
    if (!isPagedPrinting && activePrintReportKey) {
      setActivePrintReportKey(null)
    }
  }, [isPagedPrinting, activePrintReportKey])

  const handleReportPrintClick = (reportKey) => {
    const reportElement = reportRefs.current[`${reportKey}-content`]
    if (reportElement) {
      setActivePrintReportKey(reportKey)
      printSingleReport(reportElement)
    }
  }

  const getReportBookedEventId = (report) =>
    report?.prescriptionData?.bookedEventId ||
    report?.therapyReport?.bookedEventId ||
    report?.assessmentReport?.bookedEventId ||
    report?.screeningReport?.bookedEventId ||
    null

  return (
    <ConnectingLines date={date} visitNo={visitNo} timeMarkers={timeMarkers}>
      {sortedReports?.map((report, idx) => {
        if (report?.apiParam === 'meta-prescription') {
          const reportKey = `prescription-${idx}-${date}`
          return (
            <Box
              ref={(el) => {
                if (el) reportRefs.current[reportKey] = el
              }}
              id={sectionIds?.[idx]}
              key={idx}
              data-paged-report-type="prescription"
              data-paged-visit-date={date}
              sx={{
                ...reportContainerStyles,
              }}
            >
              <div 
                ref={(el) => {
                  if (el) reportRefs.current[`${reportKey}-content`] = el
                }}
                data-paged-report-type="prescription"
                data-paged-visit-date={date}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: 1,
                    gap: 1,
                  }}
                  className="hide-on-print-only"
                >
                  <Tooltip title="Print this prescription">
                    <IconButton
                      size="small"
                      onClick={() => handleReportPrintClick(reportKey, 'prescription')}
                      disabled={isPagedPrinting}
                      sx={{
                        color: 'rgba(67, 56, 202, 0.7)',
                        '&:hover': {
                          backgroundColor: 'rgba(67, 56, 202, 0.08)',
                          color: 'rgba(67, 56, 202, 0.9)',
                        },
                        '&:disabled': {
                          color: 'rgba(67, 56, 202, 0.4)',
                        },
                      }}
                    >
                      {isPagedPrinting && activePrintReportKey === reportKey ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Print sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
                <RenderNoteOverReport
                  text={
                    mentorPrivateNotes?.find(
                      (item) => item?.bookedEventId === report?.prescriptionData?.bookedEventId,
                    )?.content
                  }
                />
                <ViewOnlyPrescription
                  prescriptionData={report}
                  showHeading
                  headingText=""
                  allVitalsOfThisPatient={allVitalsOfThisPatient}
                  printableOnlyWhenPressingOnPrintButton
                  setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
                  prescriptionViewSettings={prescriptionViewSettings}
                  emrSections={report?.emrSections || []}
                  hideDownloadButton={true}
                  hideOpenPrescriptionEvent={true}
                />
                
                {/* Prescription Images - Each image on a separate A4 page */}
                {(() => {
                  const uploadedImages = report?.prescriptionData?.uploadedImages || report?.prescriptionData?.imagesUploaded || []
                  if (!uploadedImages || uploadedImages.length === 0) return null
                  
                  return uploadedImages.map((imageMediaId, imageIndex) => (
                    <Box
                      key={`prescription-image-page-${imageMediaId}-${imageIndex}`}
                      component="div"
                      className="prescription-image-page"
                      sx={{
                        width: '100%',
                        maxWidth: '794px',
                        margin: '0 auto',
                        padding: 0,
                        display: 'block',
                        '@media print': {
                          pageBreakBefore: 'always',
                          pageBreakAfter: imageIndex < uploadedImages.length - 1 ? 'always' : 'auto',
                          pageBreakInside: 'avoid',
                          width: '100%',
                          maxWidth: '100%',
                          height: '100vh',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        },
                        '@page': {
                          size: 'A4',
                          margin: 0,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          '@media print': {
                            width: '100%',
                            maxWidth: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          },
                        }}
                      >
                        <ImageWithMenu
                          imageMediaId={imageMediaId}
                          index={imageIndex}
                          menuItems={[]}
                          alt="prescription image"
                        />
                      </Box>
                    </Box>
                  ))
                })()}
                
                {/* Patient History Notes, Vitals, and Private Notes - After prescription and images */}
                {report?.prescriptionData?.bookedEventId && (
                  <EventAdditionalData
                    bookedEventId={report.prescriptionData.bookedEventId}
                    patientId={patientId}
                    clinicId={clinicId}
                    role={role}
                    hidePrivateNotes={hidePrivateNotes}
                    mentorId={mentorId}
                    patientHistoryGetApiSignature={patientHistoryGetApiSignature}
                    mentorPrivateNotes={mentorPrivateNotes}
                    useBatchedVitals={useBatchedVitals}
                    batchedVitalsLoading={batchedVitalsLoading}
                    batchedVitalsReady={batchedVitalsReady}
                    batchedVital={vitalsByBookedEventId[getReportBookedEventId(report)] || null}
                  />
                )}
              </div>
            </Box>
          )
        }
        if (report?.therapyReport) {
          const reportKey = `therapy-${idx}-${date}`
          return (
            <Box
              ref={(el) => {
                if (el) reportRefs.current[reportKey] = el
              }}
              id={sectionIds?.[idx]}
              key={idx}
              data-paged-report-type="therapy"
              data-paged-visit-date={date}
              sx={{
                ...reportContainerStyles,
              }}
            >
              <div 
                ref={(el) => {
                  if (el) reportRefs.current[`${reportKey}-content`] = el
                }}
                data-paged-report-type="therapy" 
                data-paged-visit-date={date}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: 1,
                    gap: 1,
                  }}
                  className="hide-on-print-only"
                >
                  <Tooltip title="Print this therapy report">
                    <IconButton
                      size="small"
                      onClick={() => handleReportPrintClick(reportKey, 'therapy')}
                      disabled={isPagedPrinting}
                      sx={{
                        color: 'rgba(16, 185, 129, 0.7)',
                        '&:hover': {
                          backgroundColor: 'rgba(16, 185, 129, 0.08)',
                          color: 'rgba(16, 185, 129, 0.9)',
                        },
                        '&:disabled': {
                          color: 'rgba(16, 185, 129, 0.4)',
                        },
                      }}
                    >
                      {isPagedPrinting && activePrintReportKey === reportKey ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Print sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
                <RenderNoteOverReport
                  text={
                    mentorPrivateNotes?.find(
                      (item) => item?.bookedEventId === report?.therapyReport?.bookedEventId,
                    )?.content
                  }
                />
                {report?.therapyReport?.bookedEventId && (
                  <EventAdditionalData
                    bookedEventId={report.therapyReport.bookedEventId}
                    patientId={patientId}
                    clinicId={clinicId}
                    role={role}
                    hidePrivateNotes={hidePrivateNotes}
                    mentorPrivateNotes={mentorPrivateNotes}
                    useBatchedVitals={useBatchedVitals}
                    batchedVitalsLoading={batchedVitalsLoading}
                    batchedVitalsReady={batchedVitalsReady}
                    batchedVital={vitalsByBookedEventId[getReportBookedEventId(report)] || null}
                  />
                )}
                <TherapyReportPreview
                  key={idx}
                  report={report?.therapyReport}
                  templateData={report?.therapyReportTemplate}
                  headingText={''}
                  hideSendToDoctorButton={hideSendToDoctorButton}
                  hideDownloadButton={hidePrintButton}
                  setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
                  sendReportToTriggeringPractitionerApiSignature="/api/secure/mentor/emr-reports/send-to-triggering-practitioner"
                  preloadedNextVisitSlot={
                    nextVisitSlotMap[
                      report?.therapyReport?.nextVisitReferenceBookedEventId ||
                      report?.therapyReport?.nextVisitBookedEventId
                    ] ?? null
                  }
                />
              </div>
            </Box>
          )
        }
        // One assessment report = one entry; report contains all runs and report-level fields (freeFormRichText, etc.).
        if (report?.assessmentRuns?.length || report?.assessmentReport) {
          const runs = report?.assessmentRuns ?? report?.assessmentReport?.assessmentRuns ?? []
          const reportId = report?.assessmentReport?.id
          const reportKey = `assessment-${reportId ?? 'ar'}-${date}-${idx}`
          const latestRun = runs.length > 0
            ? [...runs].sort((a, b) => (parseReportTimestamp(b?.completedAt)?.valueOf() ?? 0) - (parseReportTimestamp(a?.completedAt)?.valueOf() ?? 0))[0]
            : null
          const displayTime =
            latestRun?.completedAt ||
            report?.assessmentReport?.createdTime ||
            report?.assessmentReport?.modifiedTime
          return (
            <Box
              ref={(el) => {
                if (el) reportRefs.current[reportKey] = el
              }}
              id={sectionIds?.[idx]}
              key={reportKey}
              data-paged-report-type="assessment"
              data-paged-visit-date={date}
              sx={{
                ...reportContainerStyles,
              }}
            >
              <div
                ref={(el) => {
                  if (el) reportRefs.current[`${reportKey}-content`] = el
                }}
                data-paged-report-type="assessment"
                data-paged-visit-date={date}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: 1,
                    gap: 1,
                  }}
                  className="hide-on-print-only"
                >
                  <Tooltip title="Print this assessment report">
                    <IconButton
                      size="small"
                      onClick={() => handleReportPrintClick(reportKey, 'assessment')}
                      disabled={isPagedPrinting}
                      sx={{
                        color: 'rgba(14, 165, 233, 0.7)',
                        '&:hover': {
                          backgroundColor: 'rgba(14, 165, 233, 0.08)',
                          color: 'rgba(14, 165, 233, 0.9)',
                        },
                        '&:disabled': {
                          color: 'rgba(14, 165, 233, 0.4)',
                        },
                      }}
                    >
                      {isPagedPrinting && activePrintReportKey === reportKey ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Print sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
                <RenderNoteOverReport
                  text={
                    mentorPrivateNotes?.find(
                      (item) => item?.bookedEventId === report?.assessmentReport?.bookedEventId,
                    )?.content
                  }
                />
                {report?.assessmentReport?.bookedEventId && (
                  <EventAdditionalData
                    bookedEventId={report.assessmentReport.bookedEventId}
                    patientId={patientId}
                    clinicId={clinicId}
                    role={role}
                    hidePrivateNotes={hidePrivateNotes}
                    mentorPrivateNotes={mentorPrivateNotes}
                    useBatchedVitals={useBatchedVitals}
                    batchedVitalsLoading={batchedVitalsLoading}
                    batchedVitalsReady={batchedVitalsReady}
                    batchedVital={vitalsByBookedEventId[getReportBookedEventId(report)] || null}
                  />
                )}
                <AssessmentReportHistoryView report={report} />
              </div>
            </Box>
          )
        }
        if (report?.screeningReport) {
          const reportKey = `screening-${idx}-${date}`
          return (
            <Box 
              ref={(el) => {
                if (el) reportRefs.current[reportKey] = el
              }}
              id={sectionIds?.[idx]}
              key={idx}
              data-paged-report-type="screening"
              data-paged-visit-date={date}
              sx={{
                ...reportContainerStyles,
              }}
            >
              <div 
                ref={(el) => {
                  if (el) reportRefs.current[`${reportKey}-content`] = el
                }}
                data-paged-report-type="screening" 
                data-paged-visit-date={date}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: 1,
                    gap: 1,
                  }}
                  className="hide-on-print-only"
                >
                  <Tooltip title="Print this screening report">
                    <IconButton
                      size="small"
                      onClick={() => handleReportPrintClick(reportKey, 'screening')}
                      disabled={isPagedPrinting}
                      sx={{
                        color: 'rgba(245, 158, 11, 0.7)',
                        '&:hover': {
                          backgroundColor: 'rgba(245, 158, 11, 0.08)',
                          color: 'rgba(245, 158, 11, 0.9)',
                        },
                        '&:disabled': {
                          color: 'rgba(245, 158, 11, 0.4)',
                        },
                      }}
                    >
                      {isPagedPrinting && activePrintReportKey === reportKey ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Print sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
                <RenderNoteOverReport
                  text={
                    mentorPrivateNotes?.find(
                      (item) => item?.bookedEventId === report?.screeningReport?.bookedEventId,
                    )?.content
                  }
                />
                {report?.screeningReport?.bookedEventId && (
                  <EventAdditionalData
                    bookedEventId={report.screeningReport.bookedEventId}
                    patientId={patientId}
                    clinicId={clinicId}
                    role={role}
                    hidePrivateNotes={hidePrivateNotes}
                    mentorPrivateNotes={mentorPrivateNotes}
                    useBatchedVitals={useBatchedVitals}
                    batchedVitalsLoading={batchedVitalsLoading}
                    batchedVitalsReady={batchedVitalsReady}
                    batchedVital={vitalsByBookedEventId[getReportBookedEventId(report)] || null}
                  />
                )}
                {(() => {
                  // Prioritize passed screeningSections (from current session) over saved report sections
                  // This ensures we use the latest sections structure
                  const effectiveScreeningSections = 
                    screeningSections?.length > 0
                      ? screeningSections  // First priority: sections from current session
                      : (report?.screeningSections || 
                         report?.screeningReportTemplate?.sections || 
                         report?.screeningReport?.screeningSections || 
                         [])
                  
                  // Try multiple paths for screeningAnswers
                  const screeningAnswersFromReport = 
                    report?.screeningReport?.screeningAnswers ||
                    report?.screeningReport?.answers ||
                    report?.screeningAnswers ||
                    []
                  
                  // Try multiple paths for doctorSpecializations
                  const effectiveDoctorSpecializations = 
                    doctorSpecializations ||
                    report?.screeningReport?.doctorSpecializations ||
                    report?.screeningReport?.mentorSpecializations ||
                    report?.screeningReport?.specializations ||
                    report?.doctorSpecializations ||
                    []
                  
                  // Extract screeningForSpecializations from saved report (new field we're saving)
                  // This can be an array or a comma-separated string (depending on server format)
                  const screeningForSpecializationsFromReport = 
                    report?.screeningReport?.screeningForSpecializations ||
                    null
                  
                  // Convert to array if it's a string
                  const screeningForSpecializationsArray = screeningForSpecializationsFromReport
                    ? (Array.isArray(screeningForSpecializationsFromReport)
                        ? screeningForSpecializationsFromReport
                        : (typeof screeningForSpecializationsFromReport === 'string'
                            ? screeningForSpecializationsFromReport.split(',').filter(Boolean)
                            : []))
                    : null
                  
                  // Try multiple paths for visitType
                  const effectiveVisitType = 
                    visitType ||
                    report?.screeningReport?.visitType ||
                    report?.visitType ||
                    (report?.screeningReport?.patientMeetingSeqNumber > 1 ? 'repeat' : 'new')
                  
                  
                  return (
                    <PrintableScreeningReportComponent
                      key={idx}
                      report={report?.screeningReport}
                      templateData={report?.screeningReportTemplate}
                      emrSectionDataList={report?.emrSectionDataList}
                      screeningSections={effectiveScreeningSections}
                  headingText={''}
                  hideDownloadButton={hidePrintButton}
                  hideSendToDoctorButton={hideSendToDoctorButton}
                  setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
                  sendReportToTriggeringPractitionerApiSignature="/api/secure/mentor/emr-reports/send-to-triggering-practitioner"
                  bookedEventInfo={
                    // Pass bookedEventInfo from report if available, otherwise construct from report data
                    report?.screeningReport?.bookedEventId ? {
                      bookedEventId: report.screeningReport.bookedEventId,
                      patientMeetingSeqNumber: report?.screeningReport?.patientMeetingSeqNumber,
                      visitType: report?.screeningReport?.visitType || visitType,
                      ...report?.screeningReport,
                    } : report?.screeningReport
                  }
                    doctorSpecializations={
                      // Prioritize screeningForSpecializations from saved report, then fallback to other sources
                      screeningForSpecializationsArray ||
                      effectiveDoctorSpecializations
                    }
                    visitType={effectiveVisitType}
                    screeningForSpecializations={screeningForSpecializationsArray}
                    useDebugNumbering={false} // Show actual question text, not debug numbering
                    />
                  )
                })()}
              </div>
            </Box>
          )
        }
        return null
      })}
      <hr className="hide-on-print-only" />
    </ConnectingLines>
  )
}
